import { useNavigate } from '@tanstack/react-router';
import { useAppTheme } from 'hooks/useAppTheme';
import { useState } from 'react';
import { formatCurrency } from 'utils/currencies';

import imgEllipsis from 'components/AdvancedTable/assets/icon-ellipsis.svg';
import { CustomLoader } from 'components/CustomLoader/CustomLoader';
import { GainLoss } from 'components/GainLoss/GainLoss';

import { useContent } from '@altify/utils';
import { InfoButton } from 'components/InfoButton/InfoButton';
import { TimeFrameKey, timeFrames } from 'data/time-frames';
import {
  useFunctionalCurrency,
  useProductsWithHoldingsAndRates,
} from 'hooks/products/useProducts';
import useAppLanguage from 'hooks/useAppLanguage';
import { usePortfolioGainsAndLosses } from 'store/slices/gains-api';
import { useGetProfileQuery } from 'store/slices/profile-api';
import appUrls from 'utils/app-urls';
import { portfolioContent } from '../../portfolio.content';
import { Button, RollingAmount, Typography } from '@altify/components';
import { PortfolioSummaryEntry } from 'app/_authenticated/portfolio/types';
import { Card } from 'components/Card/Card';
import { ShowIcon } from '../../assets/icons/ShowIcon';
import { HideIcon } from '../../assets/icons/HideIcon';
import { Pressable } from 'react-native';

const imgDeposit = `${import.meta.env.VITE_ALTIFY_CDN_URL}/assets/explore/arrow-down-white.svg`;
const imgWithdraw = `${import.meta.env.VITE_ALTIFY_CDN_URL}/assets/explore/arrow-up-white.svg`;
const imgRewards = `${import.meta.env.VITE_ALTIFY_CDN_URL}/assets/explore/icon-rewards.svg`;

const PortfolioCard = ({
  timeFrame,
  togglePortfolioValues,
  hideValues = false,
  portfolioValue,
  reservedPortfolioValue,
  cashBalance,
  portfolioGainsAmount,
  portfolioGainsPercentage,
  isLoading,
  canWithdraw,
  canDeposit
}: {
  timeFrame: TimeFrameKey;
  togglePortfolioValues: (value: boolean) => void;
  hideValues: boolean;
  portfolioValue: string;
  reservedPortfolioValue: string;
  cashBalance: string;
  portfolioGainsAmount: number;
  portfolioGainsPercentage: number;
  isLoading: boolean;
  canWithdraw: boolean;
  canDeposit: boolean;
}) => {
  const { functionalCurrency, isLoading: isLoadingFunctionalCurrency } =
    useFunctionalCurrency();
    
  const localizedPortfolioContent = useContent(portfolioContent);

  const navigate = useNavigate();

  const handleFunds = (type: 'DEPOSIT' | 'WITHDRAW') => {
    navigate({ to: `/funds/${type === 'DEPOSIT' ? 'deposit' : 'withdraw'}` });
  };

  const items: PortfolioSummaryEntry[] = [
    {
      text: (
        <div className="flex flex-row items-center gap-2 mt-2">
          {localizedPortfolioContent.portfolioValue}
        </div>
      ),
      rightComponent: (
        <div className="flex flex-row text-right">
          {hideValues ? 
            <Typography size='large' variant='body' className='text-4xl leading-[2.675rem]'>*******</Typography>
          :
          <RollingAmount
            amount={portfolioValue}
            isPortfolioAmount={true}
            className="justify-end"
          />
          }
          <Pressable
            onPress={() => togglePortfolioValues(!hideValues)}
            className='mt-2 ml-2'>
            {hideValues ? <ShowIcon /> : <HideIcon />}
          </Pressable>
        </div>
      ),
    },  
    {
      text: (
        <div className="flex flex-row items-center gap-2 mt-2">
          {localizedPortfolioContent.return}
          <InfoButton
            title={localizedPortfolioContent.infoTitle}
            text={localizedPortfolioContent.infoText}
          />
        </div>
      ),
      rightComponent: (
        <div className="text-right">
          <div className="flex flex-row items-center">
            {hideValues ? 
              <>
                <GainLoss
                  value={'*****'}
                  symbol={functionalCurrency?.symbol!}
                  className={`${isLoading ? 'h-2 mb-2' : ''}`}
                  hiddenString={true}
                  hidePrefix={true}
                />
                <GainLoss
                  value={'***'}
                  isPercentage
                  background={true}
                  hiddenString={true}
                />
                
                </>
            :
              <>
                <GainLoss
                  value={isLoading ? null : portfolioGainsAmount}
                  symbol={functionalCurrency?.symbol!}
                  className={`${isLoading ? 'h-2 mb-2' : ''}`}
                />
                <GainLoss
                  value={isLoading ? null : portfolioGainsPercentage}
                  isPercentage
                  background={true}
                />
                
              </>
            }
            
          </div>
        </div>
      ),
    },
    {
      text: (
        <div className="flex flex-row items-center gap-2 mt-2">
          {localizedPortfolioContent.cashBalance}
          <InfoButton
            title={localizedPortfolioContent.cashBalanceInfoTitle}
            text={localizedPortfolioContent.cashBalanceInfoText}
          />
        </div>
      ),
      rightComponent: (
        <div className="flex flex-row text-right">
          {hideValues ? 
              <Typography size='large' variant='body' className='text-2xl leading-[1.5rem] mt-2'>******</Typography>
            :
            <RollingAmount
              amount={cashBalance}
              isPortfolioAmount={false}
            />
          }
        </div>
      ),
    },
    {
      text: (
        <div className="flex flex-row items-center gap-2 mt-2">
          {localizedPortfolioContent.inUse}
          <InfoButton
            title={localizedPortfolioContent.inUseInfoTitle}
            text={localizedPortfolioContent.inUseInfoText}
          />
        </div>
      ),
      rightComponent: (
        <div className="flex flex-row align-center items-center justify-items-center">
          {hideValues ? 
              <Typography size='large' variant='body' className='text-2xl leading-[1.5rem] mt-2'>******</Typography>
            :
            <RollingAmount
              amount={reservedPortfolioValue}
              isPortfolioAmount={false}
            />
          }
        </div>
      ),
    },
  ];

  const ActionButtons = () => (
    <div className="flex flex-row w-full gap-4 mt-6">
      {canDeposit &&
      <Button
        size="medium"
        className="px-2 flex-grow text-white"
        color='primary'
        onPress={() => {handleFunds('DEPOSIT')}}
        fullWidth={false}
      >
        <img src={imgDeposit} alt="Deposit" className='mr-2' />
        <Typography>{localizedPortfolioContent.deposit}</Typography>
      </Button>
      }
      {canWithdraw ? 
        <Button
          size="medium"
          color='secondary'
          className="px-2 flex-grow text-white"
          onPress={() => {handleFunds('WITHDRAW')}}
          fullWidth={false}
        >
          <img src={imgWithdraw} alt="withdraw" className='mr-2' />
          <Typography>{localizedPortfolioContent.withdraw}</Typography>
        </Button>
      : 
      <Button
        size="medium"
        color='secondary'
        className="px-2 flex-grow text-white"
        onPress={() => navigate({ to: `/rewards` })}
        fullWidth={false}
      >
        <img src={imgRewards} alt="Earn Rewards" className='mr-2' />
        <div>{'Earn Rewards'}</div>
      </Button>
      }
    </div>
  );

  return (
    <>
      {/* Desktop View */}
      <Card 
        className={`bg-neutral-600 flex-grow rounded-xxl mx-2 my-4 md:mt-2 lg:my-0 lg:mx-0 overflow-hidden p-4 lg:px-4`}>
        {isLoading && !portfolioValue ? (
          <div className="p-12" style={{ height: '23rem' }}>
            <CustomLoader />
          </div>
        ) : (
          <div>
            <div className="flex flex-col w-full text-header-text">
              {items.map((item: PortfolioSummaryEntry, i: number) => (
                <div
                  key={item.text}
                  className="flex flex-row w-full justify-center items-center">
                  <div
                    className="flex flex-row justify-between w-full items-center px-4"
                    style={{
                      marginBottom:
                        i === 0 ? `3rem` : 'none',
                    }}>
                    <div>{item.text}</div>
                    <div>{item.rightComponent}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="hidden md:block">
              <ActionButtons />
            </div>
          </div>
        )}

        <div className="block md:hidden mt-1">
          <ActionButtons />
        </div>
      </Card>
    </>
  );
};

export default PortfolioCard;
